import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod/v3'

import type { CreateCustomerRequest, Customer } from '@/lib/api/customer-api'
import type { PhoneConflict } from '@/lib/api/customer-error'
import { applyCustomerCreateError } from '@/lib/api/customer-error'
import { Button } from '@/components/ui/button'
import { ProvinceWardPicker } from '@/components/forms/province-ward-picker'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useCreateCustomer,
    useCustomerGroups,
    useUpdateCustomer,
} from '@/hooks/use-customer-query'
import { useProvinces, useWards } from '@/hooks/use-provinces-query'
import { CUSTOMER_NAME_MAX, customerNameLength } from '@/lib/customer-name'
import { m } from '@/lib/paraglide/messages'

interface Props {
    customer?: Customer
    onSuccess?: () => void
    onCreated?: (customer: Customer) => void
    onCancel?: () => void
}

export function CustomerForm({
    customer,
    onSuccess,
    onCreated,
    onCancel,
}: Props) {
    const { data: groups = [] } = useCustomerGroups()
    const createMutation = useCreateCustomer()
    const updateMutation = useUpdateCustomer(customer?.id ?? '')

    const provincesQuery = useProvinces()

    const schema = z.object({
        // Luật tên khách chung N5: 1–120 ký tự sau khi gộp khoảng trắng.
        name: z
            .string()
            .trim()
            .min(1, m.validation_required_name())
            .refine((v) => customerNameLength(v) <= CUSTOMER_NAME_MAX, {
                message: m.customer_name_too_long({
                    max: String(CUSTOMER_NAME_MAX),
                }),
            }),
        phone: z
            .string()
            .optional()
            .refine((v) => !v || v.length >= 8, {
                message: m.validation_phone_min(),
            }),
        email: z
            .string()
            .email(m.customer_error_email_invalid())
            .optional()
            .or(z.literal('')),
        address: z.string().optional(),
        ward: z.string().optional(),
        ward_code: z.string().optional(),
        province: z.string().optional(),
        province_code: z.string().optional(),
        tax_code: z.string().optional(),
        customer_group_id: z.string().optional(),
        notes: z.string().optional(),
        credit_limit: z.coerce.number().min(0),
    })

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: customer?.name ?? '',
            phone: customer?.phone ?? '',
            email: customer?.email ?? '',
            address: customer?.address ?? '',
            ward: customer?.ward ?? '',
            ward_code: customer?.ward_code ?? '',
            province: customer?.province ?? '',
            province_code: customer?.province_code ?? '',
            tax_code: customer?.tax_code ?? '',
            customer_group_id: customer?.customer_group_id ?? '',
            notes: customer?.notes ?? '',
            credit_limit: customer?.credit_limit ?? 0,
        },
    })

    const provinceCode = form.watch('province_code') || undefined
    const provinceText = form.watch('province')
    const wardCode = form.watch('ward_code') || undefined
    const wardText = form.watch('ward')
    const phoneValue = form.watch('phone')

    const wardsQuery = useWards(provinceCode)

    const isPending = createMutation.isPending || updateMutation.isPending

    // BE trả PHONE_EXISTS kèm id/name khách đang chiếm số — lưu tạm để render
    // link tới trang chi tiết của khách đó. Reset khi user gõ lại phone.
    const [phoneConflict, setPhoneConflict] = useState<PhoneConflict | null>(
        null,
    )
    useEffect(() => {
        setPhoneConflict(null)
    }, [phoneValue])

    const handleSubmit = form.handleSubmit(async (data) => {
        // Reconcile display text with canonical API name at submit time.
        // Legacy DB rows can hold stale spelling (e.g. "Hà Nội" vs "Thành phố Hà Nội");
        // once a code is set, prefer the API-canonical name so persisted text matches.
        const canonicalProvinceName = data.province_code
            ? (provincesQuery.data?.find((p) => p.code === data.province_code)
                  ?.name ?? data.province)
            : data.province
        const canonicalWardName = data.ward_code
            ? (wardsQuery.data?.find((w) => w.code === data.ward_code)?.name ??
              data.ward)
            : data.ward

        const payload: CreateCustomerRequest = {
            name: data.name,
            phone: data.phone || undefined,
            email: data.email || undefined,
            address: data.address || undefined,
            ward: canonicalWardName || undefined,
            ward_code: data.ward_code || undefined,
            province: canonicalProvinceName || undefined,
            province_code: data.province_code || undefined,
            tax_code: data.tax_code || undefined,
            customer_group_id: data.customer_group_id || undefined,
            notes: data.notes || undefined,
            credit_limit: data.credit_limit,
        }
        setPhoneConflict(null)
        try {
            if (customer) {
                await updateMutation.mutateAsync({
                    ...payload,
                    is_active: true,
                })
            } else {
                const res = await createMutation.mutateAsync(payload)
                if (res.data) onCreated?.(res.data)
            }
            onSuccess?.()
        } catch (err) {
            const { toast: toastMsg, phoneConflict: pc } =
                applyCustomerCreateError(err, form)
            if (pc) setPhoneConflict(pc)
            if (toastMsg) toast.error(toastMsg)
        }
    })

    return (
        <FormProvider {...form}>
            <form
                // stopPropagation: khi CustomerForm được mở trong Dialog (Radix
                // Portal) lồng bên trong một <form> khác — vd form đơn hàng —
                // event submit vẫn bubble theo cây React lên form cha và kích
                // hoạt submit của form đó. Chặn tại đây để submit chỉ thuộc về
                // form khách. Vô hại khi form đứng độc lập (không có form cha).
                onSubmit={(e) => {
                    e.stopPropagation()
                    handleSubmit(e)
                }}
                className="space-y-4"
            >
                <Field name="name" label={m.customer_name_label()} required>
                    <Input
                        id="name"
                        maxLength={CUSTOMER_NAME_MAX}
                        {...form.register('name')}
                    />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                    <Field name="phone" label={m.customer_phone_label()}>
                        <Input id="phone" {...form.register('phone')} />
                        {phoneConflict ? (
                            <p className="text-sm text-destructive">
                                {m.customer_phone_conflict_prefix()}{' '}
                                <Link
                                    to="/customers/$customerId"
                                    params={{ customerId: phoneConflict.id }}
                                    search={{ tab: 'info' }}
                                    className="underline font-medium hover:opacity-80"
                                >
                                    {phoneConflict.name}
                                </Link>
                            </p>
                        ) : null}
                    </Field>
                    <Field name="email" label="Email">
                        <Input
                            id="email"
                            type="email"
                            {...form.register('email')}
                        />
                    </Field>
                </div>

                <Field name="address" label={m.customer_address_label()}>
                    <Input id="address" {...form.register('address')} />
                </Field>

                <ProvinceWardPicker
                    value={{
                        provinceCode,
                        provinceName: provinceText,
                        wardCode,
                        wardName: wardText,
                    }}
                    onChange={(next) => {
                        form.setValue(
                            'province_code',
                            next.provinceCode ?? '',
                            {
                                shouldDirty: true,
                            },
                        )
                        form.setValue('province', next.provinceName ?? '', {
                            shouldDirty: true,
                        })
                        form.setValue('ward_code', next.wardCode ?? '', {
                            shouldDirty: true,
                        })
                        form.setValue('ward', next.wardName ?? '', {
                            shouldDirty: true,
                        })
                    }}
                />

                <div className="grid grid-cols-2 gap-4">
                    <Field name="tax_code" label={m.customer_tax_code()}>
                        <Input id="tax_code" {...form.register('tax_code')} />
                    </Field>
                    <Field
                        name="credit_limit"
                        label={m.customer_credit_limit()}
                    >
                        <Input
                            id="credit_limit"
                            type="number"
                            min={0}
                            {...form.register('credit_limit')}
                        />
                    </Field>
                </div>

                <Field name="customer_group_id" label={m.customer_group()}>
                    <Controller
                        name="customer_group_id"
                        control={form.control}
                        render={({ field }) => (
                            <Select
                                value={field.value || 'none'}
                                onValueChange={(v) =>
                                    field.onChange(v === 'none' ? '' : v)
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">
                                        {m.customer_no_group()}
                                    </SelectItem>
                                    {groups.map((g) => (
                                        <SelectItem key={g.id} value={g.id}>
                                            {g.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </Field>

                <Field name="notes" label={m.common_notes()}>
                    <Input id="notes" {...form.register('notes')} />
                </Field>

                <div className="flex justify-end gap-2 pt-2">
                    {onCancel && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onCancel}
                        >
                            {m.common_cancel()}
                        </Button>
                    )}
                    <Button type="submit" disabled={isPending}>
                        {isPending
                            ? m.common_saving()
                            : customer
                              ? m.common_update()
                              : m.common_create()}
                    </Button>
                </div>
            </form>
        </FormProvider>
    )
}
